// Portfolio Administration v1.0 PR 4b §7.8 — cure-period color band per
// Convention 8 thresholds applied to a cure-period countdown:
//   • green:  > 14 days remaining
//   • yellow: 8–14 days
//   • orange: 3–7 days
//   • red:    0–2 days
//   • grey:   expired
//
// Distinct from the generic colorBand.ts (which uses 30/15/7 boundaries
// keyed to renewal-style deadlines); cure periods have tighter NSF / NOD
// timing windows so they get their own ladder.

import { calendarDaysUntil } from "@/utils/datetime";

export type CurePeriodBand = "green" | "yellow" | "orange" | "red" | "expired";

/**
 * `tz` is the VIEWER's zone. Position 3, after the two date arguments a caller
 * already passes, so no existing call can rebind onto it.
 *
 * The one live caller, `CurePeriodStickyPanel`, supplies it. Said explicitly
 * because this parameter spent a workstream unsupplied — a destination with no
 * door, which reads as a completed fix and ships nothing.
 *
 * `curePeriodBand.test.ts` carries the specimen this helper's own test used to
 * be: it built its fixture from millisecond offsets and asserted a RANGE, so
 * it failed on main for seven hours a day and passed the other seventeen.
 */
export function curePeriodDaysRemaining(
  endIso: string | null | undefined,
  extendedEndIso?: string | null,
): number | null {
  const effective = extendedEndIso ?? endIso;
  // Was `Math.floor((ms - Date.now()) / 86_400_000)`. `floor` on a raw instant
  // difference is harsher than the `round` its siblings used: a cure period
  // ending today measured as -1 and read EXPIRED from 5pm the PREVIOUS day in
  // Phoenix. `CurePeriod.EndDate` is `startDate.AddDays(n)` off a widened
  // `DateOnly`, so it is wall-calendar and belongs on the shared rule.
  return calendarDaysUntil(effective);
}

export function curePeriodBand(daysRemaining: number | null): CurePeriodBand {
  if (daysRemaining === null) return "expired";
  if (daysRemaining < 0) return "expired";
  if (daysRemaining <= 2) return "red";
  if (daysRemaining <= 7) return "orange";
  if (daysRemaining <= 14) return "yellow";
  return "green";
}

const CURE_BAND_CLASS: Record<CurePeriodBand, string> = {
  green: "bg-status-success/15 border-status-success text-status-success",
  yellow: "bg-status-warning/15 border-status-warning text-status-warning",
  orange: "bg-status-warning/30 border-status-warning text-status-warning",
  red: "bg-status-error/15 border-status-error text-status-error",
  expired: "bg-gray-mid/15 border-gray-mid text-gray-mid",
};

export function curePeriodBandClass(band: CurePeriodBand): string {
  return CURE_BAND_CLASS[band];
}

export function curePeriodLabel(daysRemaining: number | null): string {
  if (daysRemaining === null) return "No cure period";
  if (daysRemaining < 0) return `Expired ${Math.abs(daysRemaining)} days ago`;
  if (daysRemaining === 0) return "Expires today";
  if (daysRemaining === 1) return "1 day remaining";
  return `${daysRemaining} days remaining`;
}
